/**
 * OI interval clean-up (admin-only) -- `/api/v1/oi-backfill/interval-cleanup/*`
 * (see `app.api.v1.oi_interval_cleanup`). Removes 1-minute rows that old
 * 1-minute OI backfill jobs mixed into the 5-minute OI raw tables: a read-only
 * SCAN builds a per-day plan; APPLY backs up then deletes, day by day.
 */
import api from "@/services/api";

export const CLEANUP_CONFIRM_TEXT = "DELETE 1MIN OI ROWS";

export type CleanupRunKind = "SCAN" | "APPLY";
export type CleanupRunStatus = "PENDING" | "RUNNING" | "CANCEL_REQUESTED" | "COMPLETED" | "FAILED" | "CANCELLED";
export type CleanupVerdict = "MIXED_5MIN_ON_GRID" | "MIXED_1MIN_ON_GRID" | "ONLY_1MIN" | "MIXED_UNCERTAIN";
export const ACTIVE_CLEANUP_STATUSES: CleanupRunStatus[] = ["PENDING", "RUNNING", "CANCEL_REQUESTED"];

export interface TableCounts { delete: number; keep: number; uncertain: number }

export interface ScanPlanDay {
  index: string;
  instrument_id: string;
  date: string;
  verdict: CleanupVerdict;
  trading_day: boolean | null;
  spot: TableCounts;
  chain: TableCounts;
  refetch_5min: boolean;
}

export interface ApplyResultDay {
  index: string;
  date: string;
  status: "DELETED" | "SKIPPED_CHANGED_SINCE_SCAN";
  deleted?: { spot?: number; chain?: number };
  table?: string;
  expected?: number;
  found?: number;
  refetch_5min?: boolean;
}

export type Windows = Record<string, [string, string][]>;

export interface ScanSummary {
  index_days_scanned: number;
  mixed_index_days: number;
  by_verdict: Record<CleanupVerdict, number>;
  rows_to_delete: { spot: number; chain: number };
  rows_kept_on_mixed_days: { spot: number; chain: number };
  rows_uncertain_kept: { spot: number; chain: number };
  by_month: Record<string, Record<string, Record<string, number>>>;
  refetch_windows: Windows;
  include_chain: boolean;
  backfill_jobs: number;
  one_minute_jobs: number;
}

export interface ApplySummary {
  days_processed: number;
  days_deleted: number;
  days_skipped_changed: number;
  rows_deleted: { spot: number; chain: number };
  refetch_windows: Windows;
  backup?: Record<string, { file: string; rows: number; bytes: number; sha256: string }>;
}

export interface CleanupRun {
  id: string;
  kind: CleanupRunKind;
  status: CleanupRunStatus;
  symbol_codes: string[];
  from_date: string;
  to_date: string;
  include_chain: boolean;
  scan_run_id: string | null;
  progress_done: number;
  progress_total: number;
  summary: (ScanSummary & Partial<ApplySummary>) | (ApplySummary & Partial<ScanSummary>) | null;
  backup_dir: string | null;
  error: string | null;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  plan?: Array<ScanPlanDay | ApplyResultDay>;
}

export interface ScanRequest {
  symbol_codes: string[];
  from_date: string;
  to_date: string;
  include_chain: boolean;
}

const BASE = "/oi-backfill/interval-cleanup";

export const oiIntervalCleanupService = {
  async startScan(body: ScanRequest): Promise<CleanupRun> {
    const { data } = await api.post<CleanupRun>(`${BASE}/scans`, body);
    return data;
  },
  async listRuns(limit = 20): Promise<CleanupRun[]> {
    const { data } = await api.get<CleanupRun[]>(`${BASE}/runs`, { params: { limit } });
    return data;
  },
  async getRun(id: string): Promise<CleanupRun> {
    const { data } = await api.get<CleanupRun>(`${BASE}/runs/${id}`);
    return data;
  },
  async apply(scanId: string, confirm: string): Promise<CleanupRun> {
    const { data } = await api.post<CleanupRun>(`${BASE}/runs/${scanId}/apply`, { confirm });
    return data;
  },
  async cancel(id: string): Promise<CleanupRun> {
    const { data } = await api.post<CleanupRun>(`${BASE}/runs/${id}/cancel`);
    return data;
  },
};

export default oiIntervalCleanupService;
